import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ArrowRight } from 'app/components/Icons'
import { styles, theme } from 'app/theme'
import { EXTERNAL_URL_REGEX } from 'app/utils/customFunctions'
import { useVocabularyData } from 'app/utils/vocabulary'
import { Link } from 'gatsby'
import React, { memo } from 'react'

export interface Props {
  image?: ImageProps
  index?: number
  languageCode: string
  title?: string
  URL: string
}

export const Item = memo(function Item({
  image,
  index,
  languageCode,
  title,
  URL,
}: Props) {
  if (!URL) {
    return null
  }

  const externalURL = URL && EXTERNAL_URL_REGEX.test(URL)

  const componentDOM = (
    <>
      {image ? <Image {...image} /> : null}

      <Wrapper>
        <Inner>
          <Number>{((index || 0) + 1).toString().padStart(2, '0')}</Number>

          {title ? (
            <Title>
              {title}
              <ArrowRight />
            </Title>
          ) : null}

          <CTA
            label={useVocabularyData('find-out', languageCode)}
            noActions
            variant="flat-invert"
          />
        </Inner>
      </Wrapper>
    </>
  )

  return (
    <>
      {externalURL ? (
        <ExternalLink href={URL} rel="noopener" tabIndex={0} target="_blank">
          {componentDOM}
        </ExternalLink>
      ) : (
        <Container tabIndex={0} to={URL || ''}>
          {componentDOM}
        </Container>
      )}
    </>
  )
})

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: safe center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  text-align: center;
  z-index: 2;

  @media (max-width: 1199px) {
    height: auto;
    position: relative;
    top: auto;
    left: auto;
    text-align: left;
  }
`

const Inner = styled.div``

const Number = styled.span`
  ${styles.description}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-size: 0.875rem;
  line-height: 1.0625rem;
  margin-bottom: 0.75rem;

  @media (max-width: 1199px) {
    display: none;
  }
`

const Title = styled.div`
  ${styles.title.medium}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};

  svg {
    display: none;
    width: auto;
    height: 0.75rem;
    fill: ${theme.colors.variants.neutralLight1};
    margin-left: 0.75rem;
    transform: translateX(-0.0625rem);
    transition: 0.4s ease-in-out;
  }

  @media (max-width: 1199px) {
    display: flex;
    align-items: center;
  }
`

const CTA = styled(Button)`
  margin-top: 0.75rem;

  @media (max-width: 1199px) {
    display: none;
  }
`

const Style = css`
  position: relative;
  height: 85vh;
  max-height: 30rem;
  flex: 1;
  &:nth-of-type(2) {
    img {
      transform: translateX(calc(-50vw - 0.0625rem));
    }
  }
  &:focus,
  &:focus-visible,
  &:hover {
    img {
      opacity: 1 !important;
    }

    ${CTA} {
      &:before {
        left: 100%;
      }
      &:after {
        right: 0;
      }
    }
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100%;
    object-fit: cover;
    opacity: 0 !important;
    transition: 0.6s ease-in-out;
  }

  @media (max-width: 1199px) {
    display: block;
    width: 100%;
    height: auto;
    flex: none;
    padding: 3rem 2rem;
    &:hover {
    }

    ${Title} {
      svg {
        display: block;
      }
    }

    img {
      display: none;
    }
  }
`

const ExternalLink = styled.a`
  ${Style}
`

const Container = styled(Link)`
  ${Style}
`
